// Auth state — minimalist event emitter (React Query handles server state).

import { useSyncExternalStore } from 'react'
import { get, post, patch, setAccessToken, setRefreshHint, setRefreshListener, setStaffDeniedListener, del } from './api'
import { queryClient } from './queryClient'
import { invalidateProfileStatus } from './onboarding'
import { connectNppAdmin, disconnectNppAdmin } from './nppAdmin'
import type { WorkMode } from './workMode'

export interface Shop {
  shopId: string
  id?: string
  name: string
  role: string
  tenantType?: string
  parId?: string | null
  parType?: string | null
  /** Có khi tài khoản là NHÂN VIÊN của đơn vị (role 'staff'): vai trò chủ đặt + quyền (docs/KE_HOACH_THANH_VIEN_DON_VI.md) */
  staff?: StaffInfo | null
  /** Giao diện cấp trên bật cho đơn vị (docs/KE_HOACH_CHON_GIAO_DIEN_CAP_DUOI.md) — Shop: ['sell']; máy chủ cũ: thiếu */
  workModes?: WorkMode[]
}

export interface StaffInfo {
  roleId: string | null
  roleName: string | null
  /** Quyền đã chuẩn hoá theo loại đơn vị (packages/common/src/unit-permissions.ts) */
  permissions: string[]
}

interface AuthState {
  userId: string | null
  sessionId: string | null
  email: string | null
  phone: string | null
  /** Họ tên chủ tài khoản — server chưa trả khi đăng nhập, chỉ có sau khi lưu hồ sơ. */
  name: string | null
  shops: Shop[]
  activeShopId: string | null
  /** true = đã qua bước 2 (nhập đúng mã email) hoặc hệ thống không yêu cầu mã */
  is2FaVerified: boolean
  isAuthenticated: boolean
  /** Tài khoản quản trị của một NPP — có khu Quản trị NPP (/npp), xem lib/nppAdmin.ts */
  nppAdmin: boolean
  /** Đang chờ nhập mã email (sau bước mật khẩu) — màn /2fa đọc để hiện email nhận mã và hẹn giờ gửi lại */
  loginCode: LoginCodeInfo | null
  /** Mật khẩu tạm chủ đơn vị đặt cho nhân viên — phải đổi trước khi vào làm việc (máy chủ cũng chặn) */
  mustChangePassword: boolean
}

export interface LoginCodeInfo {
  /** Email nhận mã, đã che — "ng***@pthub.vn" */
  emailMasked: string
  /** Số giây phải chờ trước khi được bấm "Gửi lại mã" */
  resendAfter: number
  /** Thời điểm gửi mã (Date.now()) */
  sentAt: number
}

// Simple event emitter for auth state changes
type Listener = (state: AuthState) => void
const listeners = new Set<Listener>()
let state: AuthState = {
  userId: null,
  sessionId: null,
  email: null,
  phone: null,
  name: null,
  shops: [],
  activeShopId: null,
  is2FaVerified: false,
  isAuthenticated: false,
  nppAdmin: false,
  loginCode: null,
  mustChangePassword: false,
}

export function getAuthState() {
  return state
}
export function subscribe(fn: Listener) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Đọc trạng thái đăng nhập trong component (tự vẽ lại khi đổi). */
export function useAuth(): AuthState {
  return useSyncExternalStore(subscribe, getAuthState)
}
function setState(patch: Partial<AuthState>) {
  const prev = state
  state = { ...state, ...patch }
  // Khoá cache (['banks'], ['orders']…) không gắn với tài khoản/shop: đổi phiên
  // hoặc đổi shop mà không xoá thì màn sau hiện dữ liệu của người trước.
  if (state.sessionId !== prev.sessionId || state.activeShopId !== prev.activeShopId) {
    queryClient.clear()
  }
  for (const l of listeners) l(state)
}

// Refresh giữa chừng (401) trả danh sách đơn vị mới — loại đơn vị có thể vừa được nâng cấp; nhân viên: quyền có thể
// vừa đổi, đơn vị có thể vừa bị gỡ (máy chủ giữ đơn vị đang mở nếu còn — Session.activeShopId)
setRefreshListener((json) => {
  if (Array.isArray(json.shops) && state.isAuthenticated) {
    const shops = json.shops as Shop[]
    const active = typeof json.activeShopId === 'string' ? json.activeShopId : null
    setState({
      shops,
      ...(active && active !== state.activeShopId ? { activeShopId: active } : {}),
      ...(typeof json.mustChangePassword === 'boolean' ? { mustChangePassword: json.mustChangePassword } : {}),
    })
  }
})

// Làm mới phiên giữa chừng: gửi đơn vị đang hiện để token mới đúng đơn vị đó
setRefreshHint(() => state.activeShopId)

// Máy chủ báo quyền nhân viên đã đổi (403 'staff.*') → đọc lại quyền để menu / nút khớp ngay
setStaffDeniedListener(() => {
  void refreshUnitAccess()
})

// ─── API contracts ────────────────────────────────────────────────
interface LoginResponse {
  sessionId: string
  accessToken: string
  expiresIn: number
  /** true = cần nhập mã gửi qua email (Super Admin bật, tài khoản có email) */
  need2Fa: boolean
  /** Luôn false — giữ từ thời TOTP cho app cũ */
  needEnroll: boolean
  twoFaMethod?: 'email'
  emailMasked?: string
  codeExpiresIn?: number
  resendAfter?: number
  shops?: Shop[]
  activeShopId?: string | null
  email?: string | null
  phone?: string | null
  nppAdmin?: boolean
  mustChangePassword?: boolean
}
interface Verify2FaResponse {
  accessToken: string
  expiresIn: number
  shops: Shop[]
  activeShopId: string | null
  nppAdmin?: boolean
  mustChangePassword?: boolean
}
interface ResendCodeResponse {
  ok: boolean
  emailMasked: string
  codeExpiresIn: number
  resendAfter: number
}

// ─── Actions ──────────────────────────────────────────────────────
/**
 * Restore session on app boot — the refresh token lives in an httpOnly cookie
 * that survives page reload. If it's still valid, /auth/refresh returns a fresh
 * access token, membership list, and we're back in.
 * Returns true if session restored, false if user needs to log in again.
 */
export async function tryRestoreSession(): Promise<boolean> {
  try {
    const r = await post<{
      accessToken: string
      expiresIn: number
      shops?: Shop[]
      activeShopId?: string | null
      email?: string | null
      phone?: string | null
      nppAdmin?: boolean
      mustChangePassword?: boolean
    }>('/auth/refresh', {})
    setAccessToken(r.accessToken)
    const shops = (r.shops ?? []) as Shop[]
    let activeShopId = r.activeShopId ?? (shops[0]?.shopId ?? null)
    if (!r.activeShopId && shops[0]?.shopId) {
      try {
        await selectShop(shops[0].shopId)
        activeShopId = shops[0].shopId
      } catch { }
    }
    setState({
      shops,
      activeShopId,
      is2FaVerified: true, // refresh only succeeds for already-verified sessions
      isAuthenticated: true,
      email: r.email ?? state.email,
      phone: r.phone ?? state.phone,
      nppAdmin: r.nppAdmin === true,
      mustChangePassword: r.mustChangePassword === true,
    })
    // Admin NPP: khôi phục luôn phiên quản trị (cookie sha_rt, hoặc xin lại từ phiên web)
    if (r.nppAdmin) await connectNppAdmin()
    return true
  } catch {
    setAccessToken(null)
    return false
  }
}

export async function login(identifier: string, password: string) {
  const isEmail = identifier.includes('@')
  // client 'web': báo máy chủ đây là bản có khu Quản trị NPP (bản cũ đăng nhập tài khoản NPP thì bị báo tải lại)
  const r = await post<LoginResponse>('/auth/login', { identifier, password, client: 'web' })
  setAccessToken(r.accessToken)
  const email = r.email ?? (isEmail ? identifier : state.email)
  const phone = r.phone ?? (!isEmail ? identifier : state.phone)

  if (!r.need2Fa) {
    await applySignedIn(r, email, phone)
  } else {
    setState({
      sessionId: r.sessionId, email, phone, nppAdmin: r.nppAdmin === true,
      loginCode: { emailMasked: r.emailMasked ?? '', resendAfter: r.resendAfter ?? 60, sentAt: Date.now() },
    })
  }
  return r
}

/** Phiên đã qua bước 2 (mật khẩu không cần mã / điện thoại duyệt mã QR) → vào app. */
async function applySignedIn(r: LoginResponse, email: string | null, phone: string | null) {
  const shops = r.shops ?? []
  let activeShopId = r.activeShopId ?? (shops[0]?.shopId ?? null)
  if (!r.activeShopId && shops[0]?.shopId) {
    try {
      await selectShop(shops[0].shopId)
      activeShopId = shops[0].shopId
    } catch { }
  }
  setState({
    sessionId: r.sessionId,
    email,
    phone,
    is2FaVerified: true,
    isAuthenticated: true,
    shops,
    activeShopId,
    nppAdmin: r.nppAdmin === true,
    loginCode: null,
    mustChangePassword: r.mustChangePassword === true,
  })
  if (r.nppAdmin) await connectNppAdmin({ fresh: true })
}

// ─── Đăng nhập bằng mã QR (docs/KE_HOACH_DANG_NHAP_QR.md) ─────────
export interface QrLoginStart {
  /** Nằm trong mã QR */
  code: string
  /** Chỉ trang này giữ — hỏi trạng thái và nhận phiên */
  pollToken: string
  expiresIn: number
  interval: number
}
export type QrLoginPoll =
  | { status: 'pending' | 'rejected' | 'expired' }
  | { status: 'scanned'; initials: string }
  | ({ status: 'approved' } & LoginResponse)

/** Máy mới: xin mã để hiện QR. */
export function startQrLogin() {
  return post<QrLoginStart>('/auth/qr/start', {})
}

/** Máy mới: hỏi trạng thái — điện thoại đã bấm "Đăng nhập" thì vào luôn. */
export async function pollQrLogin(pollToken: string): Promise<QrLoginPoll> {
  const r = await post<QrLoginPoll>('/auth/qr/poll', { pollToken })
  if (r.status === 'approved') {
    setAccessToken(r.accessToken)
    await applySignedIn(r, r.email ?? null, r.phone ?? null)
  }
  return r
}

/** Máy mới hiện cho điện thoại xem trước khi duyệt. */
export interface QrLoginDevice {
  /** "Chrome · Windows" — '' khi không đọc được */
  device: string
  ip: string | null
  createdAt: string
  expiresAt: string
}

/** Điện thoại: quét mã → thông tin máy mới. */
export function scanQrLogin(code: string) {
  return post<QrLoginDevice>('/auth/qr/scan', { code })
}

/** Điện thoại: bấm "Đăng nhập" / "Huỷ". */
export function decideQrLogin(code: string, decision: 'approve' | 'reject') {
  return post<{ ok: true }>(`/auth/qr/${decision}`, { code })
}

/** Bước 2 đăng nhập: mã 6 số gửi qua email. */
export async function verifyLoginCode(code: string) {
  if (!state.sessionId) throw new Error('Phiên đăng nhập đã hết — vui lòng đăng nhập lại')
  const r = await post<Verify2FaResponse>('/auth/2fa/verify', {
    sessionId: state.sessionId,
    code,
  })
  setAccessToken(r.accessToken)
  setState({
    is2FaVerified: true,
    isAuthenticated: true,
    shops: r.shops,
    activeShopId: r.activeShopId,
    nppAdmin: r.nppAdmin === true,
    loginCode: null,
    mustChangePassword: r.mustChangePassword === true,
  })
  if (r.nppAdmin) await connectNppAdmin({ fresh: true })
  return r
}

/** Gửi lại mã (máy chủ chặn nếu chưa đủ thời gian chờ / quá số lần gửi). */
export async function resendLoginCode() {
  if (!state.sessionId) throw new Error('Phiên đăng nhập đã hết — vui lòng đăng nhập lại')
  const r = await post<ResendCodeResponse>('/auth/2fa/resend', { sessionId: state.sessionId })
  setState({ loginCode: { emailMasked: r.emailMasked, resendAfter: r.resendAfter, sentAt: Date.now() } })
  return r
}

export async function selectShop(shopId: string): Promise<void> {
  const r = await post<{ accessToken: string; shop: Shop }>('/auth/select-shop', { shopId })
  setAccessToken(r.accessToken)
  const id = r.shop.shopId || r.shop.id || shopId
  // Máy chủ trả kèm vai trò / quyền nhân viên mới nhất của đơn vị vừa chọn
  const shops = state.shops.map((x) => (x.shopId === id ? { ...x, ...r.shop, shopId: id } : x))
  setState({ activeShopId: id, shops })
}

/**
 * Đọc lại tư cách của tài khoản trong đơn vị đang mở (GET /auth/unit-access) — sau 403 'staff.*' hoặc khi quay lại
 * tab. Nhân viên vừa bị khoá / gỡ → làm mới phiên: máy chủ chuyển sang đơn vị khác còn dùng được, hoặc đăng xuất.
 */
let unitAccessInFlight: Promise<void> | null = null
export function refreshUnitAccess(): Promise<void> {
  if (!state.isAuthenticated || !state.is2FaVerified) return Promise.resolve()
  if (unitAccessInFlight) return unitAccessInFlight
  unitAccessInFlight = (async () => {
    try {
      const r = await get<{
        staff: boolean
        shopId: string | null
        status?: 'active' | 'suspended' | 'removed'
        roleId?: string | null
        roleName?: string | null
        permissions?: string[]
        mustChangePassword?: boolean
        workModes?: WorkMode[]
      }>('/auth/unit-access')
      if (r.mustChangePassword !== undefined && r.mustChangePassword !== state.mustChangePassword) setState({ mustChangePassword: r.mustChangePassword })
      // Giao diện đơn vị (chủ lẫn nhân viên) — danh sách đơn vị cũ (máy chủ trước bản này) chưa có thì nhận ở đây
      const withModes = r.shopId && r.workModes ? state.shops.find((x) => x.shopId === r.shopId) : undefined
      if (withModes && r.workModes && withModes.workModes?.join() !== r.workModes.join()) {
        const workModes = r.workModes
        setState({ shops: state.shops.map((x) => (x.shopId === r.shopId ? { ...x, workModes } : x)) })
      }
      if (!r.staff || !r.shopId) return
      if (r.status !== 'active') {
        const ok = await tryRestoreSession()
        if (!ok) {
          setAccessToken(null)
          setState({ ...LOGGED_OUT })
        }
        return
      }
      const staff: StaffInfo = { roleId: r.roleId ?? null, roleName: r.roleName ?? null, permissions: r.permissions ?? [] }
      const cur = state.shops.find((x) => x.shopId === r.shopId)
      if (cur && JSON.stringify(cur.staff ?? null) !== JSON.stringify(staff)) {
        setState({ shops: state.shops.map((x) => (x.shopId === r.shopId ? { ...x, staff } : x)) })
      }
    } catch {
      // mạng chập chờn — lần sau đọc lại
    } finally {
      unitAccessInFlight = null
    }
  })()
  return unitAccessInFlight
}

export function updateActiveShopName(newName: string) {
  if (!newName) return
  const currentShops = state.shops.map((s) =>
    s.shopId === state.activeShopId ? { ...s, name: newName } : s
  )
  setState({ shops: currentShops })
}

export async function logout() {
  // Thu hồi cả phiên quản trị NPP (cookie sha_rt) — trước khi bỏ token web
  if (state.nppAdmin) await disconnectNppAdmin()
  try { await post('/auth/logout', {}) } catch { }
  setAccessToken(null)
  setState({ ...LOGGED_OUT })
}

const LOGGED_OUT: AuthState = {
  userId: null, sessionId: null, email: null, phone: null, name: null, shops: [], activeShopId: null,
  is2FaVerified: false, isAuthenticated: false, nppAdmin: false, loginCode: null, mustChangePassword: false,
}

/**
 * Trang vào sau đăng nhập / ở "/": admin NPP vào khu Quản trị NPP; còn lại theo loại đơn vị đang chọn.
 * (Admin NPP đồng thời là thành viên đơn vị đổi qua lại bằng nút trong menu.)
 */
export function homeRouteOf(
  s: Pick<AuthState, 'nppAdmin' | 'shops' | 'activeShopId'>,
  tenantHome: (tenantType?: string, role?: string, staff?: StaffInfo | null, workModes?: WorkMode[]) => string,
): string {
  if (s.nppAdmin) return '/npp'
  const shop = s.shops.find((x) => x.shopId === s.activeShopId) ?? s.shops[0]
  return tenantHome(shop?.tenantType, shop?.role, shop?.staff, shop?.workModes)
}

/** Mã email khi đăng nhập — hệ thống bật hay tắt (Super Admin), có tác dụng khi tài khoản có email. */
export interface LoginCodeStatus {
  method: 'email'
  required: boolean
  email: string | null
  active: boolean
}

export async function getLoginCodeStatus(): Promise<LoginCodeStatus> {
  return get<LoginCodeStatus>('/auth/2fa/status')
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await post('/auth/password/change', { currentPassword, newPassword })
  if (state.mustChangePassword) setState({ mustChangePassword: false })
}

export async function requestLinkEmail(email: string): Promise<{ ok: boolean; message: string }> {
  return post<{ ok: boolean; message: string }>('/auth/email/request-link', { email })
}

export async function verifyLinkEmail(email: string, code: string): Promise<{ ok: boolean; email: string; message: string }> {
  const r = await post<{ ok: boolean; email: string; message: string }>('/auth/email/verify-link', { email, code })
  setState({ email: r.email })
  void invalidateProfileStatus()
  return r
}

export async function unlinkEmail(): Promise<{ ok: boolean; message: string }> {
  const r = await del<{ ok: boolean; message: string }>('/auth/email/unlink')
  setState({ email: null })
  void invalidateProfileStatus()
  return r
}

/**
 * Hồ sơ chủ tài khoản (bước 1 của màn "Hoàn tất thông tin"): họ tên + email.
 * SĐT là số đăng nhập, không đổi ở đây. Email trùng tài khoản khác → 409 `auth.email_taken`.
 */
export async function updateOwnerProfile(input: { name: string; email: string }) {
  const r = await patch<{ user: { id: string; name: string | null; email: string | null; phone: string | null } }>(
    '/auth/profile',
    input,
  )
  setState({ name: r.user.name ?? input.name, email: r.user.email ?? input.email, phone: r.user.phone ?? state.phone })
  await invalidateProfileStatus()
  return r.user
}

